/**
 * RelatedActivity - Display all customer-related activities in a chronological timeline
 * Combines leads, quotes, invoices, and follow-ups into a unified activity feed
 */

import React, { useMemo, useCallback } from "react";
import { Timeline, Tag, Typography, Empty } from "antd";
import {
  FileTextOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

const { Text, Link } = Typography;

/**
 * Get icon for activity type
 */
const getActivityIcon = (type) => {
  switch (type) {
    case "lead":
      return <FileTextOutlined style={{ fontSize: "16px" }} />;
    case "quote":
      return <ShoppingCartOutlined style={{ fontSize: "16px" }} />;
    case "invoice":
      return <DollarOutlined style={{ fontSize: "16px" }} />;
    case "followup":
      return <CalendarOutlined style={{ fontSize: "16px" }} />;
    default:
      return <FileTextOutlined style={{ fontSize: "16px" }} />;
  }
};

/**
 * Get color for activity status
 */
const getActivityColor = (type, status) => {
  if (type === "followup") {
    switch (status) {
      case "completed":
        return "green";
      case "overdue":
        return "red";
      case "due":
        return "orange";
      case "pending":
        return "blue";
      case "cancelled":
        return "default";
      default:
        return "blue";
    }
  }

  if (type === "lead") {
    switch (status) {
      case "accepted":
      case "converted":
        return "green";
      case "rejected":
        return "red";
      case "pending":
        return "orange";
      default:
        return "blue";
    }
  }

  if (type === "quote") {
    switch (status) {
      case "accepted":
      case "sent":
        return "green";
      case "rejected":
      case "cancelled":
        return "red";
      case "pending":
        return "orange";
      default:
        return "blue";
    }
  }

  if (type === "invoice") {
    switch (status) {
      case "paid":
        return "green";
      case "overdue":
        return "red";
      case "partial":
        return "orange";
      case "sent":
      case "pending":
        return "blue";
      default:
        return "blue";
    }
  }

  return "blue";
};

/**
 * Format activity title
 */
const getActivityTitle = (activity) => {
  if (activity.type === "lead") {
    return `Lead ${activity.referenceNumber || activity.id || "N/A"}`;
  }
  if (activity.type === "quote") {
    return `Quote ${activity.id || "N/A"}`;
  }
  if (activity.type === "invoice") {
    return `Invoice ${activity.id || "N/A"}`;
  }
  if (activity.type === "followup") {
    return activity.title || "Follow-up";
  }
  return "Activity";
};

/**
 * Format activity description
 */
const getActivityDescription = (activity) => {
  if (activity.type === "lead") {
    const from = activity.moving_from || activity.from || "";
    const to = activity.moving_to || activity.to || "";
    if (from && to) {
      return `${from} → ${to}`;
    }
    return activity.status || "Lead";
  }
  if (activity.type === "quote") {
    const total = activity.totalCost || activity.total || 0;
    if (total > 0) {
      return `Total: R${total.toLocaleString("en-ZA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return activity.status || "Quote";
  }
  if (activity.type === "invoice") {
    const total = activity.total || 0;
    if (total > 0) {
      return `Total: R${total.toLocaleString("en-ZA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return activity.status || "Invoice";
  }
  if (activity.type === "followup") {
    return activity.description || activity.notes || "Follow-up";
  }
  return "";
};

/**
 * Get activity date for sorting
 */
const getActivityDate = (activity) => {
  if (activity.type === "followup") {
    return activity.dueDate || activity.scheduledDate || activity.createdAt;
  }
  return activity.createdAt || activity.updatedAt || new Date();
};

/**
 * RelatedActivity Component
 */
const RelatedActivity = ({
  leads = [],
  quotes = [],
  invoices = [],
  followUps = [],
}) => {
  const navigate = useNavigate();

  // Handle activity click
  const handleActivityClick = useCallback((activity) => {
    if (activity.type === "lead") {
      navigate(`/leads/${activity.id}`);
    } else if (activity.type === "quote") {
      navigate(`/quotes/${activity.id}`);
    } else if (activity.type === "invoice") {
      navigate(`/invoices/${activity.id}`);
    } else if (activity.type === "followup") {
      navigate(`/followups/${activity.id}`);
    }
  }, [navigate]);

  // Format date for display
  const formatDate = useCallback((date) => {
    if (!date) return "N/A";
    try {
      return format(new Date(date), "MMM dd, yyyy HH:mm");
    } catch {
      return "Invalid date";
    }
  }, []);

  // Combine and sort all activities chronologically
  const activities = useMemo(() => {
    const allActivities = [
      ...leads.map((lead) => ({
        type: "lead",
        id: lead.id || lead._id,
        referenceNumber: lead.referenceNumber,
        status: lead.status,
        moving_from: lead.moving_from || lead.from,
        moving_to: lead.moving_to || lead.to,
        createdAt: lead.createdAt,
        updatedAt: lead.updatedAt,
        date: getActivityDate({ type: "lead", ...lead }),
      })),
      ...quotes.map((quote) => ({
        type: "quote",
        id: quote.id || quote._id,
        status: quote.status,
        totalCost: quote.totalCost,
        total: quote.total,
        createdAt: quote.createdAt,
        updatedAt: quote.updatedAt,
        date: getActivityDate({ type: "quote", ...quote }),
      })),
      ...invoices.map((invoice) => ({
        type: "invoice",
        id: invoice.id || invoice._id,
        status: invoice.status,
        total: invoice.total,
        createdAt: invoice.createdAt,
        updatedAt: invoice.updatedAt,
        date: getActivityDate({ type: "invoice", ...invoice }),
      })),
      ...followUps.map((followUp) => ({
        type: "followup",
        id: followUp.id || followUp._id,
        title: followUp.title,
        description: followUp.description,
        notes: followUp.notes,
        status: followUp.status,
        dueDate: followUp.dueDate,
        scheduledDate: followUp.scheduledDate,
        createdAt: followUp.createdAt,
        updatedAt: followUp.updatedAt,
        date: getActivityDate({ type: "followup", ...followUp }),
      })),
    ];

    // Sort by date (newest first)
    return allActivities.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });
  }, [leads, quotes, invoices, followUps]);

  // Prepare timeline items using the new API
  const timelineItems = useMemo(() => {
    return activities.map((activity) => {
      const title = getActivityTitle(activity);
      const description = getActivityDescription(activity);
      const date = formatDate(activity.date);
      const color = getActivityColor(activity.type, activity.status);
      const icon = getActivityIcon(activity.type);

      return {
        key: `${activity.type}-${activity.id}`,
        dot: icon,
        color: color,
        children: (
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 4 }}>
              <Link
                onClick={() => handleActivityClick(activity)}
                style={{ fontWeight: 500, cursor: "pointer" }}
              >
                {title}
              </Link>
              {activity.status && (
                <Tag
                  color={color}
                  style={{ marginLeft: 8 }}
                  size="small"
                >
                  {activity.status}
                </Tag>
              )}
            </div>
            {description && (
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {description}
              </Text>
            )}
            <div>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {date}
              </Text>
            </div>
          </div>
        ),
      };
    });
  }, [activities, handleActivityClick, formatDate]);

  if (activities.length === 0) {
    return (
      <Empty
        description="No activity found"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <Timeline
      mode="left"
      items={timelineItems}
      style={{ paddingTop: 16 }}
    />
  );
};

export default RelatedActivity;
